import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Names the channels in the reader's language.
 *
 * Channel labels arrive from the API, which reads them out of the ingest mapping and
 * has no idea who is asking — so the translation happens here, keyed by the channel's
 * stable name. Anything the catalogue has no entry for keeps the label the API sent,
 * which is how a channel added to the mapping shows up on screen straight away
 * instead of waiting for a translation.
 *
 * The identity of the returned function only changes with the language, because chart
 * specs are memoised on it: a function rebuilt on every render rebuilds the uPlot
 * instances with it, and since the pointer moving is a render, that costs the cursor
 * and the zoom. `i18n.t` is used rather than the hook's own `t` for the same reason —
 * it is one stable function that reads the current language.
 */
export function useChannelLabel() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  return useCallback(
    (name: string, fallback?: string | null) =>
      i18n.t(`channels.${name}`, { defaultValue: fallback ?? name, lng: language }),
    [i18n, language],
  );
}
